import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { mockData } from '../mock';
import { Scale, Globe, Award, Users, BookOpen, TrendingUp, CheckCircle2, ArrowRight } from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <img src={mockData.logo} alt="JurisGo Logo" className="h-8 w-8 object-contain" />
              <span className="text-2xl font-semibold text-slate-900">JurisGo</span>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#about" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">About</a>
              <Link to="/trainings" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Trainings</Link>
              <Link to="/opportunities" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Opportunities</Link>
              <Link to="/blog" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Blog</Link>
              <Link to="/corporate" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Corporate</Link>
              <Link to="/wall-of-fame" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Wall of Fame</Link>
              <a href="#faq" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">FAQ</a>
            </nav>
            <div className="flex items-center space-x-3">
              <Button asChild variant="outline" className="border-green-500 text-green-600 hover:bg-green-50 transition-colors">
                <a href={mockData.whatsappGroup} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1">
                  <Users className="h-4 w-4" />
                  <span className="hidden lg:inline">Community</span>
                </a>
              </Button>
              <Button asChild variant="outline" className="border-slate-700 text-slate-700 hover:bg-slate-100 transition-colors">
                <Link to="/student/login">Student Portal</Link>
              </Button>
              <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white transition-colors">
                <a href="#enroll">Enroll Now</a>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-90"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1659355894099-b2c2b2884322?w=1200)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.15
        }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-block mb-4 px-4 py-1 bg-slate-700/50 backdrop-blur-sm rounded-full text-sm font-medium">
              International Paralegal Training
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {mockData.hero.title}
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-4 font-medium">
              {mockData.hero.subtitle}
            </p>
            <p className="text-lg text-slate-400 mb-8 leading-relaxed">
              {mockData.hero.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" asChild className="bg-white text-slate-900 hover:bg-slate-100 text-lg px-8 py-6">
                <Link to="/trainings">{mockData.hero.ctaText} <ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="border-slate-400 text-white hover:bg-slate-800 text-lg px-8 py-6">
                <a href="#about">Learn More</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-4">{mockData.about.title}</h2>
              <h3 className="text-2xl font-semibold text-slate-700 mb-6">{mockData.about.mission}</h3>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">{mockData.about.description}</p>
              
              <div className="space-y-6">
                {mockData.about.values.map((value, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-slate-700" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-slate-900 mb-1">{value.title}</h4>
                      <p className="text-slate-600">{value.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1752697588939-cba91b0c78a8?w=800" 
                  alt="Legal Education" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Opportunities Section */}
      <section id="opportunities" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">{mockData.opportunities.title}</h2>
            <p className="text-xl text-slate-600">{mockData.opportunities.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {mockData.opportunities.income.map((income, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <TrendingUp className="h-10 w-10 text-slate-700 mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{income.type}</h3>
                  <p className="text-3xl font-bold text-slate-800 mb-3">{income.range}</p>
                  <p className="text-slate-600">{income.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-semibold text-slate-900 mb-6 text-center">Work Flexibility</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {mockData.opportunities.workModes.map((mode, index) => (
                <div key={index} className="flex items-center justify-center space-x-2 p-4 bg-slate-50 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-slate-700" />
                  <span className="font-medium text-slate-800">{mode}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Training Program Section */}
      <section id="training" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">{mockData.training.title}</h2>
            <p className="text-xl text-slate-600 mb-6">{mockData.training.subtitle}</p>
            <Button size="lg" asChild className="bg-slate-900 hover:bg-slate-800">
              <Link to="/trainings">View All Training Programs <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {mockData.training.components.map((component, index) => (
              <Card key={index} className="border-slate-200 hover:border-slate-400 transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                    <BookOpen className="h-6 w-6 text-slate-700" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{component.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{component.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">What You&apos;ll Receive</h2>
            <p className="text-xl text-slate-600">Comprehensive Training Package</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.deliverables.map((deliverable, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-slate-200 hover:shadow-md transition-shadow">
                <Award className="h-8 w-8 text-slate-700 mb-3" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{deliverable.title}</h3>
                <p className="text-slate-600">{deliverable.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Who This Program Is For</h2>
            <p className="text-xl text-slate-600">Designed for Indian Legal Professionals</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.targetAudience.map((audience, index) => (
              <Card key={index} className="border-slate-200 text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <Users className="h-12 w-12 text-slate-700 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{audience.title}</h3>
                  <p className="text-slate-600">{audience.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-slate-600">Everything you need to know</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {mockData.faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white border border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-slate-700">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Enrollment CTA Section */}
      <section id="enroll" className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">{mockData.enrollment.title}</h2>
          <p className="text-2xl text-slate-300 mb-3">{mockData.enrollment.subtitle}</p>
          <p className="text-lg text-slate-400 mb-6">{mockData.enrollment.urgency}</p>
          <p className="text-xl font-semibold text-slate-200 mb-8">{mockData.enrollment.nextBatch}</p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {mockData.enrollment.features.map((feature, index) => (
              <div key={index} className="flex items-center justify-center space-x-2 p-3 bg-slate-800 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-slate-300" />
                <span className="text-sm font-medium">{feature}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 text-xl px-12 py-7">
              {mockData.enrollment.ctaText} <ArrowRight className="ml-2 h-6 w-6" />
            </Button>
            <Button size="lg" variant="outline" asChild className="border-slate-400 text-white hover:bg-slate-800 text-xl px-12 py-7">
              <a href={mockData.whatsappGroup} target="_blank" rel="noopener noreferrer">
                Join Community <Users className="ml-2 h-6 w-6" />
              </a>
            </Button>
          </div>
          <p className="text-sm text-slate-400 mt-4">Payment link will be provided upon enrollment</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-slate-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={mockData.logo} alt="JurisGo Logo" className="h-6 w-6 object-contain" />
                <span className="text-xl font-semibold text-white">JurisGo</span>
              </div>
              <p className="text-slate-400">India&apos;s Premier International Paralegal Training Platform</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Contact</h3>
              <p className="mb-2">Email: {mockData.footer.email}</p>
              <p className="mb-2">Phone: {mockData.footer.phone}</p>
              <p>Location: {mockData.footer.address}</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><a href="#about" className="hover:text-white transition-colors">About</a></li>
                <li><Link to="/trainings" className="hover:text-white transition-colors">Trainings</Link></li>
                <li><Link to="/opportunities" className="hover:text-white transition-colors">Opportunities</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/corporate" className="hover:text-white transition-colors">Corporate</Link></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
                <li><a href={mockData.whatsappGroup} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center">
                  <Users className="h-4 w-4 mr-1" /> Join Community
                </a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-700 pt-8">
            <p className="text-sm text-slate-400 mb-4">{mockData.footer.disclaimer}</p>
            <p className="text-center text-slate-500">{mockData.footer.copyright}</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Community Button */}
      <div className="fixed bottom-6 right-6 z-[10000]">
        <Button 
          asChild 
          className="bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300"
          size="lg"
        >
          <a 
            href={mockData.whatsappGroup} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center space-x-2"
          >
            <Users className="h-6 w-6" />
            <span className="hidden sm:inline font-medium">Join Community</span>
          </a>
        </Button>
      </div>
    </div>
  );
};

export default Home;
